// Cave84 — Supplier invoice import (/import/:supplierId).
//
// One flow for every supplier in utils/invoiceSuppliers.ts. The supplier's
// parser turns the PDF text into a SupplierInvoice; everything after that is
// shared:
//
//   Upload → Match (catalog lookup) → Review → Import → Done
//
// Fail-closed rules (owner's standing rule: never guess, never skip silently):
//   - a line whose producer exists under two names, or whose cuvée ties two
//     wines → import blocked (matchProblem)
//   - a NEW producer whose name overlaps an existing one ("Lafon" vs "Domaine
//     des Comtes Lafon") → the owner must choose: use existing / create new
//   - invoice lines that are not wine are listed with their amount; lines that
//     look like wine but cannot be read fail the reconciliation
//   - an invoice already in the cellar (same source + "Invoice N") → blocked
//     until explicitly overridden
//   - non-EUR invoices need an EUR rate (ECB, fetched for the invoice date,
//     editable) before anything is written
//   - a line that fails to write is reported on the Done screen, not swallowed

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import * as pdfjsLib from 'pdfjs-dist';
import { collection, getDocs, query, where } from 'firebase/firestore';
import type { Appellation, AppellationInference, Producer, Vintage, Wine, WineStyle } from '@cave84/shared-types';
import { inferAppellation } from '@cave84/shared-types';
import { pdfItemsToPageText, toPdfTextItems, inferStyle } from '@/utils/mwcInvoiceParser';
import type { Reconciliation } from '@/utils/mwcInvoiceParser';
import {
  invoiceSourceDetail, isoFromDotted, matchLots, producerCandidates, producerKnowsName, toEur,
  type CatalogProducer, type CatalogSnapshot, type ImportLine, type Matched, type SupplierImporter,
  type SupplierInvoice,
} from '@/utils/invoiceImport';
import { supplierById } from '@/utils/invoiceSuppliers';
import { cuveeKey, producerKey } from '@/utils/catalogKeys';
import { db } from '@/services/firebase';
import { createProducer, updateProducer } from '@/services/producers';
import { createWine } from '@/services/wines';
import { createVintage } from '@/services/vintages';
import { createBottles } from '@/services/bottles';
import { createAppellation, listAppellations } from '@/services/appellations';
import { createPurchaseLot } from '@/services/purchaseLots';
import { createQueueEntry } from '@/services/generationQueue';
import { useCellars } from '@/hooks/useCellars';
import { Money } from '@/components/Money';

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).href;

type MatchedLine = Matched<ImportLine>;

/** The owner's answer for a new producer that resembles an existing name. */
type Decision =
  | { kind: 'existing'; producerId: string }
  | { kind: 'sibling'; name: string }   // same producer as another line of this invoice
  | { kind: 'new' };

async function extractTextFromPdf(file: File): Promise<string> {
  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const parts: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const content = await (await pdf.getPage(i)).getTextContent();
    parts.push(pdfItemsToPageText(toPdfTextItems(content.items)));
  }
  return parts.join('\n');
}

async function loadCatalog(): Promise<CatalogSnapshot> {
  const [p, w, v] = await Promise.all([
    getDocs(collection(db, 'producers')),
    getDocs(collection(db, 'wines')),
    getDocs(query(collection(db, 'vintages'), where('bottle_count', '>=', 0))),
  ]);
  return {
    producers: p.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Producer, 'id'>) })),
    wines: w.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Wine, 'id'>) })),
    vintages: v.docs.map(d => ({ id: d.id, ...(d.data() as Omit<Vintage, 'id'>) })),
  };
}

/** Bottles already carrying this supplier + invoice reference. */
async function countAlreadyImported(supplier: SupplierImporter, invoiceNumber: string): Promise<number> {
  if (!invoiceNumber) return 0;
  const snap = await getDocs(query(collection(db, 'bottles'),
    where('purchase_source_detail', '==', invoiceSourceDetail(invoiceNumber))));
  return snap.docs.filter(d => d.data().purchase_source === supplier.source).length;
}

/** ECB reference rate (units of `currency` per 1 EUR) for a date, via Frankfurter. */
async function fetchEcbRate(isoDate: string, currency: string): Promise<{ rate: number; date: string }> {
  const res = await fetch(`https://api.frankfurter.dev/v1/${isoDate}?base=EUR&symbols=${currency}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const json = (await res.json()) as { date: string; rates: Record<string, number> };
  const rate = json.rates[currency];
  if (!rate) throw new Error('rate missing');
  return { rate, date: json.date };
}

// ---------------------------------------------------------------------------
// Firestore writer
// ---------------------------------------------------------------------------

/** What this run has created, so later lines reuse it (see MWC Moët ×4 bug). */
interface ImportSession {
  producers: Map<string, string>;  // producerKey → producer id
  wines: Map<string, string>;      // `${producerId}|${cuveeKey}|${style}` → wine id
  vintages: Map<string, string>;   // `${wineId}|${year}` → vintage id
}

async function resolveAppellationId(inf: AppellationInference, cache: Map<string, string>): Promise<string> {
  const key = inf.name.trim().toLowerCase();
  if (!key) return '';
  const existing = cache.get(key);
  if (existing) return existing;
  const created = await createAppellation({
    name: inf.name.trim(),
    hierarchy_path: [inf.region, inf.subregion, inf.name].filter(Boolean).join(' > '),
    classification_level: inf.classification_level,
    country: inf.country,
    region: inf.region,
    ...(inf.subregion ? { subregion: inf.subregion } : {}),
  } as Omit<Appellation, 'id' | 'usage_count'>);
  cache.set(key, created.id);
  return created.id;
}

interface WriteContext {
  supplier: SupplierImporter;
  invoice: SupplierInvoice;
  cellarId: string;
  purchaseDate: Date;
  eurPerUnit: number;
  session: ImportSession;
  appellationCache: Map<string, string>;
}

async function importLine(line: MatchedLine, ctx: WriteContext): Promise<{ bottleIds: string[]; createdWine: boolean }> {
  const inf = line.inferredAppellation;
  let createdWine = false;

  const pKey = producerKey(line.producerName);
  let producerId = line.existingProducerId ?? ctx.session.producers.get(pKey) ?? null;
  if (!producerId) {
    const created = await createProducer({
      name: line.producerName,
      aliases: line.producerRaw !== line.producerName ? [line.producerRaw] : [],
      country: inf.country,
      region: inf.region,
      ...(inf.subregion ? { subregion: inf.subregion } : {}),
      top_cuvees: [],
    });
    producerId = created.id;
  }
  ctx.session.producers.set(pKey, producerId);

  const wKey = `${producerId}|${cuveeKey(line.cuvee, [line.producerName])}|${line.inferredStyle}`;
  let wineId = line.existingWineId ?? ctx.session.wines.get(wKey) ?? null;
  if (!wineId) {
    const created = await createWine({
      producer_id: producerId,
      appellation_id: await resolveAppellationId(inf, ctx.appellationCache),
      cuvee_name: line.cuvee,
      style: line.inferredStyle,
      grape_composition: [],
    });
    wineId = created.id;
    createdWine = true;
    await createQueueEntry('wine_analysis', wineId, 'high');
  }
  ctx.session.wines.set(wKey, wineId);

  const vKey = `${wineId}|${line.year}`;
  let vintageId = line.existingVintageId ?? ctx.session.vintages.get(vKey) ?? null;
  if (!vintageId) {
    const created = await createVintage({
      wine_id: wineId,
      year: line.year,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      vintage_quality_rating: null as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      drinking_window_initial: null as any,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      drinking_window_current: null as any,
    });
    vintageId = created.id; // createVintage queues a market_price_refresh itself
  }
  ctx.session.vintages.set(vKey, vintageId);

  const isEur = ctx.invoice.currency === 'EUR';
  const bottleIds = await createBottles({
    vintage_id: vintageId,
    wine_id: wineId,
    format: line.format,
    purchase_date: ctx.purchaseDate,
    purchase_source: ctx.supplier.source,
    purchase_source_detail: invoiceSourceDetail(ctx.invoice.invoiceNumber),
    cost_landed: line.costPerBottle,
    cost_currency: ctx.invoice.currency,
    cost_eur_at_purchase: isEur ? line.costPerBottle : toEur(line.costPerBottle, ctx.eurPerUnit),
    current_cellar_id: ctx.cellarId,
    is_special_occasion: false,
    fill_level: 'into_neck',
    label_condition: 'perfect',
    capsule_condition: 'perfect',
    in_OWC: line.inOwc,
    notes: line.bottleNote,
  }, line.qty);

  return { bottleIds, createdWine };
}

// ---------------------------------------------------------------------------
// Small UI pieces
// ---------------------------------------------------------------------------

const inputCls = 'w-full px-3 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm focus:outline-none focus:border-gold';

const STYLE_DOT: Record<WineStyle, string> = {
  red: '#C49255', white: '#e8e4c8', rose: '#e8a0a0',
  sparkling: '#a8c5da', sweet: '#d4a8c5', fortified: '#8E663A', orange: '#d4a067',
};

function Spinner({ label }: { label: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 space-y-3">
      <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      <p className="text-sm text-text-muted">{label}</p>
    </div>
  );
}

function UploadStep({ supplier, onParsed }: {
  supplier: SupplierImporter;
  onParsed: (inv: SupplierInvoice) => void;
}) {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isDrag, setIsDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function processFile(file: File) {
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError(`Please upload the ${supplier.name} invoice as a .pdf file.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const inv = supplier.parse!(await extractTextFromPdf(file));
      if (inv.lines.length === 0 && inv.excluded.length === 0) {
        setError(`No invoice lines found. Make sure this is a ${supplier.name} invoice.`);
        return;
      }
      onParsed(inv);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-1">Upload {supplier.name} invoice</h2>
        <p className="text-xs text-text-muted">{supplier.description}</p>
      </div>
      <div
        role="button"
        tabIndex={0}
        onDragOver={e => { e.preventDefault(); setIsDrag(true); }}
        onDragLeave={() => setIsDrag(false)}
        onDrop={e => { e.preventDefault(); setIsDrag(false); const f = e.dataTransfer.files[0]; if (f) void processFile(f); }}
        onClick={() => !loading && inputRef.current?.click()}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); if (!loading) inputRef.current?.click(); } }}
        className={`border-2 border-dashed rounded-xl px-8 py-12 text-center cursor-pointer transition-colors ${
          isDrag ? 'border-gold bg-gold/5' : 'border-border-medium hover:border-border-strong bg-surface-2'
        } ${loading ? 'pointer-events-none opacity-60' : ''}`}
      >
        {loading ? (
          <p className="text-sm text-text-muted">Reading PDF…</p>
        ) : (
          <>
            <svg className="w-8 h-8 mx-auto mb-3 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M12 18v-6" /><path d="m9 15 3-3 3 3" />
            </svg>
            <p className="text-sm font-medium text-text-primary">Drop PDF here or click to browse</p>
            <p className="text-xs text-text-muted mt-1">{supplier.name} invoice PDF</p>
          </>
        )}
        <input ref={inputRef} type="file" accept=".pdf" className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) void processFile(f); }} />
      </div>
      {error && <p className="text-sm text-red-400">{error}</p>}
    </div>
  );
}

function ReconciliationPanel({ recon, currency }: { recon: Reconciliation; currency: SupplierInvoice['currency'] }) {
  const [open, setOpen] = useState(recon.hasErrors || recon.hasWarnings);
  const failed = recon.checks.filter(c => !c.ok);
  const unread = recon.checks.filter(c => c.invoice == null);
  const tone = recon.hasErrors
    ? { box: 'bg-red-950/25 border-red-800/60', text: 'text-red-300', icon: '✕' }
    : recon.hasWarnings
      ? { box: 'bg-amber-950/20 border-amber-800/50', text: 'text-amber-300', icon: '!' }
      : { box: 'bg-emerald-950/20 border-emerald-800/50', text: 'text-emerald-400', icon: '✓' };
  const headline = recon.hasErrors
    ? `Invoice reconciliation FAILED — ${failed.length} check${failed.length > 1 ? 's' : ''} did not balance`
    : recon.hasWarnings
      ? `Reconciled with ${failed.length} warning${failed.length > 1 ? 's' : ''}`
      : 'Reconciled against invoice totals — all checks balance';
  const ccy = (unit: string) => (unit === 'eur' ? 'EUR' : currency);

  return (
    <div className={`rounded-lg border ${tone.box}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left">
        <span className={`${tone.text} font-bold w-4 text-center`} aria-hidden>{tone.icon}</span>
        <span className={`text-sm font-medium ${tone.text} flex-1`}>{headline}</span>
        {unread.length > 0 && <span className="text-xs text-text-muted">{unread.length} field(s) not found</span>}
        <span className="text-text-muted text-xs">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-text-muted">
                <th className="py-1.5 text-left font-medium w-6" />
                <th className="py-1.5 text-left font-medium">Check</th>
                <th className="py-1.5 text-right font-medium w-28">Invoice</th>
                <th className="py-1.5 text-right font-medium w-28">Computed</th>
                <th className="py-1.5 text-right font-medium w-24">Δ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/60">
              {recon.checks.map(c => {
                const missing = c.invoice == null;
                const mark = missing ? '–' : c.ok ? '✓' : c.severity === 'error' ? '✕' : '!';
                const markCls = missing ? 'text-text-muted' : c.ok ? 'text-emerald-500'
                  : c.severity === 'error' ? 'text-red-400' : 'text-amber-400';
                const fmt = (v: number, sign = false) => c.unit === 'count'
                  ? `${sign && v > 0 ? '+' : ''}${v}`
                  : <Money value={v} currency={ccy(c.unit)} maximumFractionDigits={2} minWidthCh={0} sign={sign} />;
                return (
                  <tr key={c.id} className={!c.ok && !missing ? 'font-medium' : ''}>
                    <td className={`py-1.5 text-center ${markCls}`} title={c.hint}>{mark}</td>
                    <td className="py-1.5 text-text-secondary">
                      {c.label}
                      {missing && <span className="text-text-muted ml-1.5">(not found in PDF)</span>}
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-text-muted">{missing ? '—' : fmt(c.invoice!)}</td>
                    <td className="py-1.5 text-right tabular-nums text-text-secondary">{fmt(c.computed)}</td>
                    <td className={`py-1.5 text-right tabular-nums ${missing || c.ok ? 'text-text-muted' : markCls}`}>
                      {missing ? '—' : c.delta === 0 ? '0' : fmt(c.delta, true)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {recon.hasErrors && (
            <p className="text-xs text-red-300 mt-2.5 leading-relaxed">
              These totals are read directly off the invoice. A mismatch means a line was dropped or
              misread — importing now would leave bottles missing from the cellar. Check the PDF
              against the table below before overriding.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** Inline producer / cuvée editor for a line that will create a new wine. */
function LineEditor({ line, onSave, onCancel }: {
  line: ImportLine;
  onSave: (producerName: string, cuvee: string) => void;
  onCancel: () => void;
}) {
  const [producer, setProducer] = useState(line.producerName);
  const [cuvee, setCuvee] = useState(line.cuvee);
  return (
    <div className="mt-2 space-y-1.5" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()} role="presentation">
      <input className={inputCls} value={producer} onChange={e => setProducer(e.target.value)} aria-label="Producer" placeholder="Producer" />
      <input className={inputCls} value={cuvee} onChange={e => setCuvee(e.target.value)} aria-label="Wine" placeholder="Wine / appellation / climat" />
      <div className="flex gap-2">
        <button type="button" disabled={!producer.trim()}
          onClick={() => onSave(producer.trim(), cuvee.trim())}
          className="px-3 py-1 rounded bg-gold text-black text-xs font-semibold disabled:opacity-40">Apply</button>
        <button type="button" onClick={onCancel}
          className="px-3 py-1 rounded border border-border-medium text-xs text-text-secondary">Cancel</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flow
// ---------------------------------------------------------------------------

type Step = 'upload' | 'matching' | 'review' | 'importing' | 'done';

interface DoneState {
  totalBottles: number;
  newWines: number;
  cellarName: string;
  failures: string[];
}

function ImportFlow({ supplier }: { supplier: SupplierImporter }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: cellars = [] } = useCellars();

  const [step, setStep] = useState<Step>('upload');
  const [invoice, setInvoice] = useState<SupplierInvoice | null>(null);
  const [baseLines, setBaseLines] = useState<ImportLine[]>([]);
  const [catalog, setCatalog] = useState<CatalogSnapshot>({ producers: [], wines: [], vintages: [] });
  const [catalogError, setCatalogError] = useState('');
  const [decisions, setDecisions] = useState<Record<string, Decision>>({});
  const [editing, setEditing] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cellarId, setCellarId] = useState('');
  const [dd, setDd] = useState('');
  const [mm, setMm] = useState('');
  const [yyyy, setYyyy] = useState('');
  const [overrideRecon, setOverrideRecon] = useState(false);
  const [alreadyImported, setAlreadyImported] = useState(0);
  const [overrideDuplicate, setOverrideDuplicate] = useState(false);
  const [rateText, setRateText] = useState('');                 // units per 1 EUR, as typed
  const [rateInfo, setRateInfo] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [done, setDone] = useState<DoneState | null>(null);

  const currency = invoice?.currency ?? supplier.currency;
  const isEur = currency === 'EUR';

  // -- Upload → match ---------------------------------------------------------
  const handleParsed = useCallback(async (inv: SupplierInvoice) => {
    setInvoice(inv);
    setBaseLines(inv.lines);
    setSelected(new Set(inv.lines.map(l => l.key)));
    setDecisions({});
    const [d = '', m = '', y = ''] = inv.invoiceDate.split('.');
    const now = new Date();
    setDd(d || String(now.getDate()).padStart(2, '0'));
    setMm(m || String(now.getMonth() + 1).padStart(2, '0'));
    setYyyy(y || String(now.getFullYear()));
    setStep('matching');
    try {
      const [cat, dup] = await Promise.all([loadCatalog(), countAlreadyImported(supplier, inv.invoiceNumber)]);
      setCatalog(cat);
      setAlreadyImported(dup);
      setCatalogError('');
    } catch (e) {
      // Without the catalog every line would look new and create duplicates —
      // block the import instead of "treating all as new".
      setCatalogError(`Could not read the catalog (${e instanceof Error ? e.message : String(e)}). Import is blocked — reload and try again.`);
    }
    setStep('review');
  }, [supplier]);

  // -- ECB rate for non-EUR invoices ------------------------------------------
  const isoDate = /^\d{4}$/.test(yyyy) ? isoFromDotted(`${dd}.${mm}.${yyyy}`) : '';
  useEffect(() => {
    if (isEur || step !== 'review' || !isoDate) return;
    let cancelled = false;
    setRateInfo('Fetching ECB rate…');
    fetchEcbRate(isoDate, currency)
      .then(r => {
        if (cancelled) return;
        setRateText(r.rate.toFixed(4));
        setRateInfo(`ECB reference rate of ${r.date.split('-').reverse().join('.')}`);
      })
      .catch(() => { if (!cancelled) setRateInfo('Could not fetch the ECB rate — enter it by hand.'); });
    return () => { cancelled = true; };
  }, [isEur, step, isoDate, currency]);

  const unitsPerEur = parseFloat(rateText.replace(',', '.'));
  const eurPerUnit = isEur ? 1 : unitsPerEur > 0 ? 1 / unitsPerEur : 0;

  // -- Decisions + matching ---------------------------------------------------
  // A decision belongs to the producer NAME as printed (after any edit), so it
  // covers every line of that producer on the invoice.
  const decisionKey = useMemo(() => {
    const m: Record<string, string> = {};
    for (const l of baseLines) m[l.key] = producerKey(l.producerName);
    return m;
  }, [baseLines]);
  const decisionFor = (lineKey: string): Decision | undefined => decisions[decisionKey[lineKey] ?? ''];
  const decide = (lineKey: string, d: Decision | null) => setDecisions(prev => {
    const k = decisionKey[lineKey] ?? '';
    const next = { ...prev };
    if (d) next[k] = d; else delete next[k];
    return next;
  });

  const decidedLines = useMemo(() => baseLines.map(l => {
    const d = decisions[producerKey(l.producerName)];
    if (d?.kind === 'existing') {
      const p = catalog.producers.find(x => x.id === d.producerId);
      return p ? { ...l, producerName: p.name } : l;
    }
    if (d?.kind === 'sibling') return { ...l, producerName: d.name };
    return l;
  }), [baseLines, decisions, catalog.producers]);

  const lines: MatchedLine[] = useMemo(
    () => matchLots(decidedLines, catalog.producers, catalog.wines, catalog.vintages),
    [decidedLines, catalog]);

  /** For each unmatched line: catalog producers and other invoice names it may mean. */
  const candidates = useMemo(() => {
    const out: Record<string, { catalog: CatalogProducer[]; siblings: string[] }> = {};
    for (const l of lines) {
      if (l.existingProducerId || l.matchProblem || decisions[decisionKey[l.key] ?? '']) continue;
      const cat = producerCandidates(l.producerName, catalog.producers);
      const key = producerKey(l.producerName);
      // Other NEW producers on this invoice (a sibling already in the catalog
      // is offered above as "Use …").
      const siblings = [...new Set(lines
        .filter(o => o.key !== l.key && !o.existingProducerId && producerKey(o.producerName) !== key)
        .map(o => o.producerName))]
        .filter(n => producerCandidates(l.producerName, [{ id: n, name: n, aliases: [] }]).length > 0);
      if (cat.length || siblings.length) out[l.key] = { catalog: cat, siblings };
    }
    return out;
  }, [lines, decisions, decisionKey, catalog.producers]);

  const editLine = (key: string, producerName: string, cuvee: string) => {
    setBaseLines(prev => prev.map(l => {
      if (l.key !== key) return l;
      const style = inferStyle(cuvee, producerName);
      return { ...l, producerName, cuvee, inferredStyle: style,
        inferredAppellation: inferAppellation(cuvee, { producerName, style }) };
    }));
    setEditing(null);
  };

  // -- Gate -------------------------------------------------------------------
  const selectedLines = lines.filter(l => selected.has(l.key));
  const blocked = selectedLines.filter(l => l.matchProblem);
  const undecided = selectedLines.filter(l => candidates[l.key]);
  const dateOk = isoDate !== '' && !Number.isNaN(new Date(isoDate).getTime());
  const recon = invoice?.reconciliation ?? { checks: [], hasErrors: false, hasWarnings: false };
  const canConfirm = !!invoice && cellarId !== '' && selectedLines.length > 0 && !catalogError
    && blocked.length === 0 && undecided.length === 0 && dateOk && eurPerUnit > 0
    && (!recon.hasErrors || overrideRecon)
    && (alreadyImported === 0 || overrideDuplicate);

  const totalQty = selectedLines.reduce((s, l) => s + l.qty, 0);
  const totalCost = selectedLines.reduce((s, l) => s + l.costPerBottle * l.qty, 0);
  // Distinct wines to create — lots of the same new wine share one wine doc.
  const newCount = new Set(selectedLines.filter(l => !l.existingWineId)
    .map(l => `${producerKey(l.producerName)}|${cuveeKey(l.cuvee, [l.producerName])}|${l.inferredStyle}`)).size;

  // -- Import -----------------------------------------------------------------
  async function handleConfirm() {
    if (!invoice || !canConfirm) return;
    setStep('importing');
    setProgress({ current: 0, total: selectedLines.length });
    const failures: string[] = [];

    // Teach the catalog the invoice's short names ("Leflaive" → Domaine Leflaive)
    // so the next invoice matches without asking again.
    const aliasAdds = new Map<string, string[]>();
    for (const l of selectedLines) {
      const d = decisionFor(l.key);
      if (d?.kind !== 'existing') continue;
      const p = catalog.producers.find(x => x.id === d.producerId);
      if (!p || producerKnowsName(p, l.producerRaw)) continue;
      const list = aliasAdds.get(p.id) ?? [...(p.aliases ?? [])];
      if (!list.some(a => producerKey(a) === producerKey(l.producerRaw))) list.push(l.producerRaw);
      aliasAdds.set(p.id, list);
    }
    for (const [id, aliases] of aliasAdds) {
      try { await updateProducer(id, { aliases }); }
      catch (e) { failures.push(`Alias for producer ${id} not saved: ${e instanceof Error ? e.message : String(e)}`); }
    }

    const appellationCache = new Map<string, string>();
    for (const a of await listAppellations()) appellationCache.set(a.name.trim().toLowerCase(), a.id);
    const ctx: WriteContext = {
      supplier, invoice, cellarId, eurPerUnit, appellationCache,
      purchaseDate: new Date(`${isoDate}T12:00:00`),
      session: { producers: new Map(), wines: new Map(), vintages: new Map() },
    };

    let totalBottles = 0;
    let newWines = 0;
    const allBottleIds: string[] = [];
    let totalLanded = 0;
    for (let i = 0; i < selectedLines.length; i++) {
      const line = selectedLines[i]!;
      setProgress({ current: i + 1, total: selectedLines.length });
      try {
        const { bottleIds, createdWine } = await importLine(line, ctx);
        if (createdWine) newWines++;
        allBottleIds.push(...bottleIds);
        totalBottles += bottleIds.length;
        totalLanded += line.costPerBottle * bottleIds.length;
      } catch (e) {
        console.error(`Failed to import ${line.ref}:`, e);
        failures.push(`${line.ref} · ${line.producerName} ${line.year} ${line.cuvee}: ${e instanceof Error ? e.message : String(e)}`);
      }
    }

    if (allBottleIds.length > 0) {
      try {
        await createPurchaseLot({
          source: supplier.source,
          source_reference: invoice.invoiceNumber,
          purchase_date: ctx.purchaseDate,
          total_cost_landed: Math.round(totalLanded * 100) / 100,
          currency: invoice.currency,
          notes: invoice.purchaseLotNote
            + (isEur ? '' : ` — 1 EUR = ${unitsPerEur.toFixed(4)} ${invoice.currency}`),
          bottles: allBottleIds,
        });
      } catch (e) {
        failures.push(`Purchase lot record not saved: ${e instanceof Error ? e.message : String(e)}`);
      }
    }

    await Promise.all(['bottles', 'vintages', 'wines', 'producers']
      .map(k => queryClient.invalidateQueries({ queryKey: [k] })));

    setDone({ totalBottles, newWines, failures, cellarName: cellars.find(c => c.id === cellarId)?.name ?? cellarId });
    setStep('done');
  }

  const reset = () => {
    setStep('upload'); setInvoice(null); setBaseLines([]); setDecisions({}); setDone(null);
    setOverrideRecon(false); setOverrideDuplicate(false); setAlreadyImported(0); setRateText(''); setRateInfo('');
  };

  // -- Render -------------------------------------------------------------------
  const numInputCls = 'w-14 px-2 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm text-center focus:outline-none focus:border-gold tabular-nums';
  const allSelected = lines.length > 0 && selected.size === lines.length;
  const toggle = (key: string) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <div className="min-h-screen bg-surface-0">
      <div className="sticky top-0 z-10 bg-surface-1 border-b border-border-medium px-4 py-3 flex items-center gap-3">
        <Link to="/import" className="text-text-muted hover:text-text-primary text-sm" aria-label="Back to Import">←</Link>
        <div className="flex-1">
          <h1 className="text-base font-semibold text-text-primary">{supplier.name} Import</h1>
          <p className="text-xs text-text-muted">{supplier.kind}</p>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {step === 'upload' && <UploadStep supplier={supplier} onParsed={inv => void handleParsed(inv)} />}
        {step === 'matching' && <Spinner label="Matching wines to cellar…" />}
        {step === 'importing' && <Spinner label={`Importing line ${progress.current} of ${progress.total}…`} />}

        {step === 'review' && invoice && (
          <div className="space-y-5">
            <div>
              <h2 className="text-base font-semibold text-text-primary mb-0.5">
                Review import
                <span className="text-text-muted font-normal ml-2 text-sm">
                  {invoice.invoiceNumber ? `Invoice ${invoice.invoiceNumber}` : 'Invoice number not found'}
                </span>
              </h2>
              <p className="text-sm text-text-muted">
                {lines.length} wine line{lines.length !== 1 ? 's' : ''} · {lines.reduce((s, l) => s + l.qty, 0)} bottles
                {invoice.excluded.length > 0 && ` · ${invoice.excluded.length} other line${invoice.excluded.length > 1 ? 's' : ''}`}
              </p>
            </div>

            {catalogError && (
              <div className="bg-red-950/25 border border-red-800/60 rounded-lg p-3 text-sm text-red-300">{catalogError}</div>
            )}

            <ReconciliationPanel recon={recon} currency={invoice.currency} />

            {alreadyImported > 0 && (
              <label className="flex items-start gap-2.5 bg-red-950/25 border border-red-800/60 rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={overrideDuplicate} onChange={e => setOverrideDuplicate(e.target.checked)}
                  className="accent-red-500 w-4 h-4 mt-0.5 cursor-pointer" />
                <span className="text-sm text-red-300">
                  Invoice {invoice.invoiceNumber} is already in the cellar — {alreadyImported} bottle{alreadyImported > 1 ? 's' : ''} carry
                  this invoice reference. Importing again creates duplicates. Tick only to add lines that were
                  left out last time (and untick the rest below).
                </span>
              </label>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <span className="block text-xs text-text-muted mb-1">Assign to cellar *</span>
                <select value={cellarId} onChange={e => setCellarId(e.target.value)} className={inputCls}>
                  <option value="">Select cellar…</option>
                  {cellars.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <span className="block text-xs text-text-muted mb-1">Purchase date (DD / MM / YYYY)</span>
                <div className="flex items-center gap-1.5">
                  <input type="number" min={1} max={31} value={dd} onChange={e => setDd(e.target.value.padStart(2, '0'))} className={numInputCls} aria-label="Day" />
                  <span className="text-text-muted">/</span>
                  <input type="number" min={1} max={12} value={mm} onChange={e => setMm(e.target.value.padStart(2, '0'))} className={numInputCls} aria-label="Month" />
                  <span className="text-text-muted">/</span>
                  <input type="number" min={2000} max={2099} value={yyyy} onChange={e => setYyyy(e.target.value)}
                    className="w-20 px-2 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm text-center focus:outline-none focus:border-gold tabular-nums" aria-label="Year" />
                </div>
              </div>
              {!isEur && (
                <div className="sm:col-span-2">
                  <span className="block text-xs text-text-muted mb-1">Exchange rate * — {currency} per 1 EUR</span>
                  <div className="flex items-center gap-3">
                    <input value={rateText} onChange={e => { setRateText(e.target.value); setRateInfo('Entered by hand'); }}
                      inputMode="decimal" className="w-28 px-2 py-1.5 rounded-md border border-border-medium bg-surface-0 text-text-primary text-sm tabular-nums focus:outline-none focus:border-gold"
                      aria-label={`${currency} per EUR`} />
                    <span className={`text-xs ${eurPerUnit > 0 ? 'text-text-muted' : 'text-red-400'}`}>
                      {eurPerUnit > 0 ? rateInfo : rateInfo || 'Required — bottle costs are stored in EUR as well'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border-medium overflow-x-auto">
              <table className="text-sm w-full">
                <thead className="bg-surface-3">
                  <tr>
                    <th className="px-3 py-2.5 text-center w-8">
                      <input type="checkbox" checked={allSelected} aria-label="Select all"
                        onChange={() => setSelected(allSelected ? new Set() : new Set(lines.map(l => l.key)))}
                        className="accent-gold w-4 h-4 cursor-pointer" />
                    </th>
                    <th className="hidden sm:table-cell px-3 py-2.5 text-left font-medium text-text-muted w-16">Ref</th>
                    <th className="px-3 py-2.5 text-left font-medium text-text-muted min-w-[210px]">Wine</th>
                    <th className="px-3 py-2.5 text-center font-medium text-text-muted w-12">Qty</th>
                    <th className="px-3 py-2.5 text-right font-medium text-text-muted w-24">{supplier.linePriceLabel}</th>
                    <th className="px-3 py-2.5 text-right font-medium text-text-muted w-24">Cost/btl</th>
                    {!isEur && <th className="px-3 py-2.5 text-right font-medium text-text-muted w-24">EUR/btl</th>}
                    <th className="px-3 py-2.5 text-center font-medium text-text-muted w-28">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {lines.map(line => {
                    const isSelected = selected.has(line.key);
                    const cand = candidates[line.key];
                    const decision = decisionFor(line.key);
                    return (
                      <tr key={line.key} onClick={() => toggle(line.key)}
                        className={`cursor-pointer transition-colors align-top ${isSelected ? 'bg-surface-1 hover:bg-surface-2' : 'bg-surface-0/50 opacity-50 hover:opacity-70'}`}>
                        <td className="px-3 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <input type="checkbox" checked={isSelected} onChange={() => toggle(line.key)}
                            aria-label={`Select ${line.ref}`} className="accent-gold w-4 h-4 cursor-pointer" />
                        </td>
                        <td className="hidden sm:table-cell px-3 py-3 tabular-nums text-text-muted whitespace-nowrap">{line.ref}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-start gap-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: STYLE_DOT[line.inferredStyle] }} />
                            <div className="min-w-0 flex-1">
                              <p className="text-text-primary font-medium">
                                {line.producerName}
                                {line.producerName !== line.producerRaw && (
                                  <span className="text-text-muted font-normal text-xs ml-1.5">(invoice: {line.producerRaw})</span>
                                )}
                              </p>
                              <p className="text-text-muted text-xs mt-0.5">
                                <span className="sm:hidden">{line.ref} · </span>
                                {line.year === 1001 ? 'NV' : line.year} · {line.cuvee || '—'}
                                {line.format !== '750ml' && <span className="ml-1.5 text-text-secondary">· {line.format}</span>}
                              </p>
                              {!line.existingWineId && (
                                <p className="text-xs mt-0.5 flex flex-wrap items-center gap-1.5">
                                  <span className="text-text-muted">
                                    {line.inferredAppellation.name || line.inferredAppellation.region || 'Unknown region'}
                                    {line.inferredAppellation.classification_level
                                      ? ` · ${line.inferredAppellation.classification_level.replace(/_/g, ' ')}` : ''}
                                  </span>
                                  {line.inferredAppellation.confidence === 'low' && (
                                    <span className="px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300 border border-amber-700/50">verify appellation</span>
                                  )}
                                  {editing !== line.key && (
                                    <button type="button" className="text-gold hover:underline"
                                      onClick={e => { e.stopPropagation(); setEditing(line.key); }}>edit name</button>
                                  )}
                                </p>
                              )}
                              {editing === line.key && (
                                <LineEditor line={baseLines.find(b => b.key === line.key)!}
                                  onSave={(p, c) => editLine(line.key, p, c)} onCancel={() => setEditing(null)} />
                              )}
                              {cand && (
                                <div className="mt-2 rounded border border-amber-700/50 bg-amber-950/20 p-2 space-y-1.5" onClick={e => e.stopPropagation()} role="presentation">
                                  <p className="text-xs text-amber-300">
                                    &quot;{line.producerName}&quot; is not in the cellar, but looks like:
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {cand.catalog.map(p => (
                                      <button key={p.id} type="button"
                                        onClick={() => decide(line.key, { kind: 'existing', producerId: p.id })}
                                        className="px-2 py-0.5 rounded border border-gold/50 text-xs text-gold hover:bg-gold/10">
                                        Use {p.name}
                                      </button>
                                    ))}
                                    {cand.siblings.map(n => (
                                      <button key={n} type="button"
                                        onClick={() => decide(line.key, { kind: 'sibling', name: n })}
                                        className="px-2 py-0.5 rounded border border-gold/50 text-xs text-gold hover:bg-gold/10">
                                        Same as {n} (this invoice)
                                      </button>
                                    ))}
                                    <button type="button"
                                      onClick={() => decide(line.key, { kind: 'new' })}
                                      className="px-2 py-0.5 rounded border border-border-medium text-xs text-text-secondary hover:bg-surface-3">
                                      New producer &quot;{line.producerName}&quot;
                                    </button>
                                  </div>
                                </div>
                              )}
                              {decision && (
                                <p className="text-xs mt-1 text-text-muted" onClick={e => e.stopPropagation()} role="presentation">
                                  {decision.kind === 'new' ? 'New producer — confirmed.' : 'Producer chosen.'}{' '}
                                  <button type="button" className="text-gold hover:underline"
                                    onClick={() => decide(line.key, null)}>undo</button>
                                </p>
                              )}
                              {line.matchProblem && <p className="text-xs mt-1 text-red-300">{line.matchProblem}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 tabular-nums text-center text-text-secondary">{line.qty}</td>
                        <td className="px-3 py-3 tabular-nums text-right text-text-secondary">
                          <Money value={line.linePrice} currency={currency} minWidthCh={7} />
                        </td>
                        <td className="px-3 py-3 tabular-nums text-right text-gold font-medium">
                          <Money value={line.costPerBottle} currency={currency} maximumFractionDigits={2} minWidthCh={7} />
                        </td>
                        {!isEur && (
                          <td className="px-3 py-3 tabular-nums text-right text-text-secondary">
                            {eurPerUnit > 0
                              ? <Money value={toEur(line.costPerBottle, eurPerUnit)} currency="EUR" maximumFractionDigits={2} minWidthCh={7} />
                              : '—'}
                          </td>
                        )}
                        <td className="px-3 py-3 text-center whitespace-nowrap">
                          {line.matchProblem ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-red-900/40 text-red-300 border border-red-700/50">blocked</span>
                          ) : !line.existingWineId ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-amber-900/40 text-amber-300 border border-amber-700/50">NEW</span>
                          ) : line.isNew ? (
                            <span className="px-2 py-0.5 rounded text-xs bg-sky-900/40 text-sky-300 border border-sky-700/50">new vintage</span>
                          ) : (
                            <span className="px-2 py-0.5 rounded text-xs bg-emerald-900/40 text-emerald-400 border border-emerald-700/50">matched</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-surface-3 border-t border-border-medium">
                    <td className="hidden sm:table-cell" />
                    <td colSpan={2} className="px-3 py-2.5 text-sm text-text-muted">{selectedLines.length} of {lines.length} lines selected</td>
                    <td className="px-3 py-2.5 text-center text-sm font-medium text-text-secondary">{totalQty}</td>
                    <td className="px-3 py-2.5" />
                    <td className="px-3 py-2.5 text-right text-sm font-bold text-gold">
                      <Money value={totalCost} currency={currency} maximumFractionDigits={2} minWidthCh={7} />
                    </td>
                    {!isEur && (
                      <td className="px-3 py-2.5 text-right text-sm text-text-secondary">
                        {eurPerUnit > 0 && <Money value={toEur(totalCost, eurPerUnit)} currency="EUR" maximumFractionDigits={2} minWidthCh={7} />}
                      </td>
                    )}
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>

            {invoice.excluded.length > 0 && (
              <div className="rounded-lg border border-border-medium bg-surface-1 p-3 space-y-1.5">
                <p className="text-sm text-text-secondary font-medium">Invoice lines that will NOT be imported</p>
                {invoice.excluded.map(e => (
                  <div key={e.ref} className={`text-xs flex flex-wrap gap-x-3 ${e.kind === 'unparsed' ? 'text-red-300' : 'text-text-muted'}`}>
                    <span className="tabular-nums">{e.ref}</span>
                    <span dir="auto" className="flex-1 min-w-0">{e.description}</span>
                    {e.amount !== 0 && <Money value={e.amount} currency={currency} maximumFractionDigits={2} minWidthCh={0} />}
                    <span className="w-full">{e.reason}</span>
                  </div>
                ))}
              </div>
            )}

            {newCount > 0 && (
              <div className="bg-amber-950/20 border border-amber-800/50 rounded-lg p-3">
                <p className="text-sm text-amber-300 font-medium mb-0.5">{newCount} new wine{newCount > 1 ? 's' : ''} will be created</p>
                <p className="text-xs text-text-muted">Wine analysis and market price lookup will be queued automatically.</p>
              </div>
            )}

            {undecided.length > 0 && (
              <p className="text-sm text-amber-300">
                Choose the producer for {undecided.map(l => l.ref).join(', ')} before importing.
              </p>
            )}

            {blocked.length > 0 && (
              <div className="bg-red-950/25 border border-red-800/60 rounded-lg p-3">
                <p className="text-sm text-red-300 font-medium">
                  Import blocked — {blocked.length} line{blocked.length > 1 ? 's' : ''} cannot be matched with certainty.
                </p>
              </div>
            )}

            {recon.hasErrors && (
              <label className="flex items-start gap-2.5 bg-red-950/25 border border-red-800/60 rounded-lg p-3 cursor-pointer">
                <input type="checkbox" checked={overrideRecon} onChange={e => setOverrideRecon(e.target.checked)}
                  className="accent-red-500 w-4 h-4 mt-0.5 cursor-pointer" />
                <span className="text-sm text-red-300">
                  Import anyway — I have checked the PDF and accept that the parsed lines do not reconcile to the invoice totals.
                </span>
              </label>
            )}

            <div className="flex justify-between pt-1">
              <button type="button" onClick={reset}
                className="px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-primary text-sm hover:bg-surface-3">
                ← Back
              </button>
              <button type="button" onClick={() => void handleConfirm()} disabled={!canConfirm}
                className="px-6 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90 disabled:opacity-40">
                Import {totalQty} bottle{totalQty !== 1 ? 's' : ''} →
              </button>
            </div>
          </div>
        )}

        {step === 'done' && done && invoice && (
          <div className="space-y-5">
            <div className="text-center py-6">
              <h2 className="text-base font-semibold text-text-primary">
                {done.failures.length ? 'Import finished with errors' : 'Import complete'}
              </h2>
              <p className="text-sm text-text-muted mt-1">
                {done.totalBottles} bottle{done.totalBottles !== 1 ? 's' : ''} added to <span className="text-text-secondary">{done.cellarName}</span>
              </p>
            </div>
            <div className="bg-surface-2 border border-border-medium rounded-lg divide-y divide-border-subtle text-sm">
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-text-muted">Invoice</span>
                <span className="text-text-secondary">{supplier.name} · {invoice.invoiceNumber}</span>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <span className="text-text-muted">Bottles imported</span>
                <span className="text-emerald-400 font-medium">{done.totalBottles}</span>
              </div>
              {done.newWines > 0 && (
                <div className="flex justify-between px-4 py-2.5">
                  <span className="text-text-muted">New wines created</span>
                  <span className="text-amber-400">{done.newWines}</span>
                </div>
              )}
            </div>
            {done.failures.length > 0 && (
              <div className="bg-red-950/25 border border-red-800/60 rounded-lg p-3 space-y-1">
                <p className="text-sm text-red-300 font-medium">These were NOT written — add them by hand or re-import with only these lines selected:</p>
                {done.failures.map(f => <p key={f} className="text-xs text-red-200/90">{f}</p>)}
              </div>
            )}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={reset}
                className="flex-1 px-4 py-2 rounded-md border border-border-medium bg-surface-2 text-text-secondary text-sm hover:bg-surface-3">
                Import another
              </button>
              <button type="button" onClick={() => navigate('/inventory')}
                className="flex-1 px-4 py-2 rounded-md bg-gold text-black font-semibold text-sm hover:bg-gold/90">
                View inventory
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function InvoiceImportPage() {
  const { supplierId } = useParams();
  const supplier = supplierById(supplierId);

  if (!supplier || supplier.status !== 'ready' || !supplier.parse) {
    return (
      <section className="max-w-2xl mx-auto px-6 py-20 text-center">
        <h1 className="text-xl text-text-primary mb-3">{supplier ? `${supplier.name} — coming soon` : 'Unknown importer'}</h1>
        <p className="text-text-secondary mb-8 text-sm">
          {supplier ? 'This importer needs a sample invoice before it can read the PDF.' : 'There is no importer at this address.'}
        </p>
        <Link to="/import" className="text-gold hover:underline text-sm">Back to Import</Link>
      </section>
    );
  }
  // key: switching supplier remounts the flow with fresh state.
  return <ImportFlow key={supplier.id} supplier={supplier} />;
}
